import IBAN from 'iban';

export class IBANValidator {
  /**
   * Validates an IBAN using the official algorithm
   */
  static validate(iban: string): boolean {
    if (!iban) {
      return false;
    }

    // Clean the input (remove spaces, convert to uppercase)
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();

    return IBAN.isValid(cleanIban);
  }

  /**
   * Formats IBAN with spaces every 4 characters
   */
  static format(iban: string): string {
    if (!iban) return '';

    const cleanIban = iban.replace(/\s/g, '').toUpperCase();

    // Use the library's print format function
    try {
      return IBAN.printFormat(cleanIban, ' ');
    } catch (error) {
      console.error(error);
      // If formatting fails, manually add spaces every 4 chars
      return cleanIban.replace(/(.{4})/g, '$1 ').trim();
    }
  }

  /**
   * Get IBAN country code
   */
  static getCountryCode(iban: string): string | null {
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();
    if (cleanIban.length >= 2) {
      return cleanIban.substring(0, 2);
    }
    return null;
  }

  /**
   * Check if IBAN belongs to a specific country
   */
  static isFromCountry(iban: string, countryCode: string): boolean {
    const ibanCountry = this.getCountryCode(iban);
    return ibanCountry === countryCode.toUpperCase();
  }

  /**
   * Get detailed validation info
   */
  static getValidationDetails(iban: string): {
    isValid: boolean;
    countryCode: string | null;
    length: number;
    formatted: string;
    errors: string[];
  } {
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();
    const errors: string[] = [];

    // Basic length check
    if (cleanIban.length < 15) {
      errors.push('IBAN too short (minimum 15 characters)');
    }
    if (cleanIban.length > 34) {
      errors.push('IBAN too long (maximum 34 characters)');
    }

    // Country code check
    const countryCode = this.getCountryCode(cleanIban);
    if (!countryCode || !/^[A-Z]{2}$/.test(countryCode)) {
      errors.push('Invalid country code format');
    }

    // Check digits format
    if (
      cleanIban.length >= 4 &&
      !/^[A-Z]{2}[0-9]{2}/.test(cleanIban.substring(0, 4))
    ) {
      errors.push('Invalid check digits format');
    }

    const isValid = errors.length === 0 && IBAN.isValid(cleanIban);

    if (!isValid && errors.length === 0) {
      errors.push('Invalid IBAN checksum');
    }

    return {
      isValid,
      countryCode,
      length: cleanIban.length,
      formatted: this.format(iban),
      errors,
    };
  }
}
